import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { tokenStorage } from '@/core/services/tokenStorage';

function isNativePlatform() {
  try {
    return (
      typeof Capacitor !== 'undefined' &&
      typeof Capacitor.isNativePlatform === 'function' &&
      Capacitor.isNativePlatform()
    );
  } catch {
    return false;
  }
}

function getCookieDomainAndFlags() {
  if (typeof window === 'undefined') return { domain: '', secure: '' };
  const hostname = window.location.hostname;
  if (hostname.endsWith('homyos.app')) {
    return { domain: '; domain=.homyos.app', secure: '; secure' };
  }
  // In local development (127.0.0.1 or localhost), omit domain and secure flags
  return { domain: '', secure: '' };
}

function encodeUserHint(user) {
  try {
    const jsonStr = typeof user === 'string' ? user : JSON.stringify(user);
    if (typeof TextEncoder !== 'undefined' && typeof btoa !== 'undefined') {
      const bytes = new TextEncoder().encode(jsonStr);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return btoa(binary);
    }
  } catch (err) {
    console.error('Error encoding user hint for cookie:', err);
  }
  return null;
}

/**
 * Mirror JWT session data to secure native preferences on mobile, localStorage,
 * and cross-subdomain cookies for marketing site session detection.
 */
export async function saveAuthSession(token, user, refreshToken) {
  if (token) {
    tokenStorage.setAccessToken(token);
  }
  if (user) {
    tokenStorage.setUser(user);
  }
  if (refreshToken) {
    tokenStorage.setRefreshToken(refreshToken);
  }

  // Cross-subdomain cookie propagation for marketing site session detection
  if (token && typeof document !== 'undefined') {
    const { domain, secure } = getCookieDomainAndFlags();
    document.cookie = `homy_token=${encodeURIComponent(token)}; path=/; max-age=2592000; samesite=lax${domain}${secure}`;

    if (user) {
      const hint = encodeUserHint(user);
      if (hint) {
        document.cookie = `homy_user_hint=${encodeURIComponent(hint)}; path=/; max-age=2592000; samesite=lax${domain}${secure}`;
      }
    }
  }
}

/**
 * Remove mirrored JWT session data from native preferences, localStorage, and cookies.
 */
export async function clearAuthSession() {
  tokenStorage.clearTokens();

  if (typeof document !== 'undefined') {
    const { domain } = getCookieDomainAndFlags();
    document.cookie = `homy_token=; path=/; max-age=0; samesite=lax${domain}`;
    document.cookie = `homy_user_hint=; path=/; max-age=0; samesite=lax${domain}`;
    if (domain) {
      document.cookie = 'homy_token=; path=/; max-age=0; samesite=lax';
      document.cookie = 'homy_user_hint=; path=/; max-age=0; samesite=lax';
    }
  }
}

/**
 * Check secure preferences for a JWT token and copy it back to localStorage if lost.
 */
export async function restoreSessionToLocalStorage() {
  if (isNativePlatform()) {
    try {
      const { value: token } = await Preferences.get({ key: 'homy_token' });
      const { value: user } = await Preferences.get({ key: 'homy_user' });
      const { value: refreshToken } = await Preferences.get({ key: 'homy_refresh_token' });

      if (token && !tokenStorage.getAccessToken()) {
        tokenStorage.setAccessToken(token);
        if (user) {
          tokenStorage.setUser(user);
        }
        if (refreshToken) {
          tokenStorage.setRefreshToken(refreshToken);
        }
        return { token, user: typeof user === 'string' ? JSON.parse(user) : user, refreshToken };
      }
    } catch (err) {
      console.error('Error restoring session from secure preferences:', err);
    }
  }
  return null;
}

export function getRefreshToken() {
  return tokenStorage.getRefreshToken();
}

export function setRefreshToken(refreshToken) {
  tokenStorage.setRefreshToken(refreshToken);
}

